import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Monitor, Smartphone, CornerUpLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ from_name: "", reply_to: "" });
  const [active, setActive] = useState(null);
  const [html, setHtml] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [device, setDevice] = useState("desktop");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/email-templates`, { withCredentials: true });
        setTemplates(data.templates || []);
        setMeta({ from_name: data.from_name, reply_to: data.reply_to });
        if (data.templates?.length) setActive(data.templates[0].key);
      } catch {
        toast.error("Could not load email templates");
      } finally {
        setLoadingList(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!active) return;
    (async () => {
      setLoadingPreview(true);
      try {
        const { data } = await axios.get(`${API}/admin/email-templates/${active}/preview`, {
          withCredentials: true,
          responseType: "text",
        });
        // Rewrite the logo to load from our own backend so the preview isn't
        // blocked by the live site's hotlink protection (real emails are unaffected).
        const rewritten = (data || "").replace(
          /src="[^"]*\/images\/logo-full\.png"/g,
          `src="${API}/email-assets/logo-full.png"`
        );
        setHtml(rewritten);
      } catch {
        toast.error("Could not load preview");
        setHtml("");
      } finally {
        setLoadingPreview(false);
      }
    })();
  }, [active]);

  const activeTpl = templates.find((t) => t.key === active);

  return (
    <div className="mt-6" data-testid="email-templates">
      <div className="mb-5">
        <h2 className="font-display text-2xl font-bold text-slate-900">Email Templates</h2>
        <p className="mt-1 text-sm text-slate-500">
          Preview exactly how each customer email looks before it goes out. Showing sample data.
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-1.5 font-semibold text-slate-700" data-testid="email-from-name">
            <Mail className="h-3.5 w-3.5 text-blaze" /> From: {meta.from_name}
          </span>
          {meta.reply_to && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-1.5 font-semibold text-slate-700" data-testid="email-reply-to">
              <CornerUpLeft className="h-3.5 w-3.5 text-blaze" /> Reply-to: {meta.reply_to}
            </span>
          )}
        </div>
      </div>

      {loadingList ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
          {/* Template list */}
          <div className="space-y-2" data-testid="email-template-list">
            {templates.map((t) => (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                className={`flex w-full flex-col items-start gap-0.5 rounded-xl border p-3.5 text-left transition-all active:scale-[0.99] ${
                  active === t.key ? "border-blaze bg-blue-50" : "border-line bg-white hover:border-blaze/40"
                }`}
                data-testid={`email-tpl-${t.key}`}
              >
                <span className={`text-sm font-bold ${active === t.key ? "text-blaze" : "text-slate-900"}`}>{t.label}</span>
                <span className="text-xs leading-snug text-slate-500">{t.description}</span>
              </button>
            ))}
          </div>

          {/* Preview pane */}
          <div className="rounded-2xl border border-line bg-slate-100 p-3 sm:p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-bold text-slate-900">{activeTpl?.label}</p>
              <div className="flex gap-1 rounded-lg border border-line bg-white p-0.5">
                <button
                  onClick={() => setDevice("desktop")}
                  className={`rounded-md p-1.5 ${device === "desktop" ? "bg-blaze text-white" : "text-slate-400"}`}
                  data-testid="email-preview-desktop"
                  title="Desktop width"
                >
                  <Monitor className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDevice("mobile")}
                  className={`rounded-md p-1.5 ${device === "mobile" ? "bg-blaze text-white" : "text-slate-400"}`}
                  data-testid="email-preview-mobile"
                  title="Mobile width"
                >
                  <Smartphone className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex justify-center overflow-hidden">
              {loadingPreview ? (
                <div className="flex h-[400px] items-center justify-center text-slate-400">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <iframe
                  title="email-preview"
                  srcDoc={html}
                  className="h-[640px] w-full rounded-lg border border-line bg-white shadow-sm transition-all"
                  style={{ maxWidth: device === "mobile" ? 390 : "100%" }}
                  data-testid="email-preview-frame"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
